import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/locale/es';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import Resume from './Resume';
import TableAlert from '../../TableAlert';
import { default as ClockingsService } from 'services/Clockings';
import GpsPopup from 'pages/Clockings/GpsPopup';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

moment.locale('es');

const Styled = styled.div`

    .top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-transform: uppercase;

        h3 {
            font-size: 20px;
            margin: 0;
        }

        .filters {
            
            select {
                display: inline-block;
                width: 200px;
            }
        }
    }

    .table-wrapper {
        margin-top: 10px;

        table {

            thead {

                tr {

                    th {
                        font-size: 12px;
                        background: var(--plenoil-blue);
                        color: white;
                        font-weight: 400;
                        text-transform: uppercase;
                        padding: 5px 10px;
                        white-space: nowrap;
                        text-align: center;

                        &:nth-child(2) {
                            text-align: left;
                            width: 200px;
                        }

                        &.active {
                            color: white !important;
                        }

                        &:nth-child(12) {
                            text-align: center;
                        }

                        &:last-of-type {
                            width: 300px;
                        }
                    }
                }
            }

            tbody {

                tr {

                    td {
                        color: var(--plenoil-gray);
                        font-size: 12px;

                        &:nth-child(2) {
                            
                            b {
                                color: black;
                            }

                            .intervals {
                                white-space: nowrap;

                                span {
                                    letter-spacing: -1px;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export default function MonthsTable(props) {
    const { dispenser } = props;

	const [loading, setLoading] = useState(true);
    const [sortDirection, setSortDirection] = useState('desc');
	const [sortField, setSortField] = useState('date');
	const [skeletonRows, setSkeletonRows] = useState(5);
	const [schedules, setSchedules] = useState([]);
    const [month, setMonth] = useState(moment().format('MM'));
    const [year, setYear] = useState(moment().format('YYYY'));
    
    const years = [moment().subtract(1, 'years').format('YYYY'), moment().format('YYYY')];
	
	useEffect(() => {
		if ( !dispenser?.id ) return;

		let getSchedules = async () => {
			setLoading(true);
			setSchedules([]);

            let date = year + '-' + month + '-01';
            let dateFrom = moment(date).startOf('month').format('YYYY-MM-DD');
            let dateTo = moment(date).endOf('month').format('YYYY-MM-DD');

			let schedules = await ClockingsService.getDispenserSchedules(dispenser?.id, dateFrom, dateTo, {
				sort: sortField,
				direction: sortDirection
			});

			setLoading(false);
			setSkeletonRows(schedules?.length >= 5 ? schedules.length : 5);
			setSchedules(schedules);
		}
		getSchedules();
	}, [dispenser?.id, year, month, sortField, sortDirection]);

    const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

    const setYearMonth = (value) => {
        let [y, m] = value.split('-');
        setYear(y);
        setMonth(m);
    }

    const downloadExcel = async () => {
        if (!schedules.length) return;

        // Lógica de colores igual que TableAlert.jsx
        const getTableAlertColor = (interval, what) => {
            if (!interval) return null;
            if (what === 'start_at') {
                if (interval.alerts?.isEarlyEntered) return '003366'; // blue
                if (interval.alerts?.isCorrectEntered) return '28a745'; // green
                if (interval.alerts?.notEntered) return 'dc3545'; // red
                if (interval.alerts?.isLateEnter) return 'fd7e14'; // orange
            }
            if (what === 'end_at') {
                if (interval.alerts?.isCorrectLeave) return '28a745'; // green
                if (interval.alerts?.isEarlyLeave) return 'fd7e14'; // orange
                if (interval.alerts?.isLateLeave) return '003366'; // blue
                if (interval.alerts?.notLeave) return 'dc3545'; // red
            }
            return null;
        };

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Horarios');

        worksheet.columns = [
            { header: 'Día', key: 'dia', width: 12 },
            { header: 'Estación', key: 'estacion', width: 25 },
            { header: 'Horario', key: 'horario', width: 25 },
            { header: 'Entrada 1', key: 'entrada1', width: 12 },
            { header: 'Salida 1', key: 'salida1', width: 12 },
            { header: 'Entrada 2', key: 'entrada2', width: 12 },
            { header: 'Salida 2', key: 'salida2', width: 12 },
            { header: 'Registradas', key: 'registradas', width: 12 },
            { header: 'Balance', key: 'balance', width: 12 },
            { header: 'N. Destino', key: 'ndestino', width: 18 },
            { header: 'Motivo', key: 'motivo', width: 18 },
            { header: 'Notas', key: 'notas', width: 18 },
        ];

        // Add rows
        schedules.forEach(el => {
            let intervalos = (el.intervals || []).map(i =>
                `${moment('2024-11-07 ' + i.start_at).format('HH:mm')} - ${moment('2024-11-07 ' + i.end_at).format('HH:mm')}`
            ).join(' / ');

            // Buscar los valores de chronometer_start_at y chronometer_end_at
            let entrada1 = '';
            let salida1 = '';
            let entrada2 = '';
            let salida2 = '';
            if (el.intervals?.[0]) {
                entrada1 = el.intervals[0]["chronometer_start_at"] && el.intervals[0]["chronometer_start_at"] !== null ? moment('2024-11-07 ' + el.intervals[0]["chronometer_start_at"]).format('HH:mm') : '--:--';
                salida1 = el.intervals[0]["chronometer_end_at"] && el.intervals[0]["chronometer_end_at"] !== null ? moment('2024-11-07 ' + el.intervals[0]["chronometer_end_at"]).format('HH:mm') : '--:--';
            } else {
                entrada1 = '--:--';
                salida1 = '--:--';
            }
            if (el.intervals?.[1]) {
                entrada2 = el.intervals[1]["chronometer_start_at"] && el.intervals[1]["chronometer_start_at"] !== null ? moment('2024-11-07 ' + el.intervals[1]["chronometer_start_at"]).format('HH:mm') : '--:--';
                salida2 = el.intervals[1]["chronometer_end_at"] && el.intervals[1]["chronometer_end_at"] !== null ? moment('2024-11-07 ' + el.intervals[1]["chronometer_end_at"]).format('HH:mm') : '--:--';
            } else {
                entrada2 = '--:--';
                salida2 = '--:--';
            }

            worksheet.addRow({
                dia: moment(el.date).format('DD-MM-YYYY'),
                estacion: el.station_name,
                horario: intervalos,
                entrada1,
                salida1,
                entrada2,
                salida2,
                registradas: el.chronometers_total !== 0 ? el.chronometers_total : '',
                balance: el.balance !== 0 ? el.balance : '',
                ndestino: el.type === 'relocation' ? el.station_name : '',
                motivo: el.reason ?? '',
                notas: el.notes ?? ''
            });
        });

        // Header style (plenoil-blue)
        worksheet.getRow(1).eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '003366' }
            };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });

        // Body style (gray text) + TableAlert colors
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            row.eachCell((cell, colNumber) => {
                // Default body style
                cell.font = { color: { argb: 'FF6C757D' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                // Entrada/Salida colors
                // 4: Entrada 1, 5: Salida 1, 6: Entrada 2, 7: Salida 2
                const el = schedules[rowNumber-2];
                if (colNumber === 4) {
                    const interval = el.intervals?.[0];
                    const color = getTableAlertColor(interval, 'start_at');
                    if (color) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: color }
                        };
                        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    }
                }
                if (colNumber === 5) {
                    const interval = el.intervals?.[0];
                    const color = getTableAlertColor(interval, 'end_at');
                    if (color) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: color }
                        };
                        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    }
                }
                if (colNumber === 6) {
                    const interval = el.intervals?.[1];
                    const color = getTableAlertColor(interval, 'start_at');
                    if (color) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: color }
                        };
                        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    }
                }
                if (colNumber === 7) {
                    const interval = el.intervals?.[1];
                    const color = getTableAlertColor(interval, 'end_at');
                    if (color) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: color }
                        };
                        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    }
                }
            });
        });

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `horarios_${month}_${year}.xlsx`);
    }

    let monthName = moment(year + '-' + (month.length < 2 ? '0' + month : month) + '-01').format('MMMM');
    let yearShort = moment(year + '-01-01').format('YY');

    let yearMonth = year + '-' + month;

	return (
		<Styled>
            <div className="top">
			    <h3>{monthName} / {yearShort}</h3>
                <div className="filters">
                    <select className="form-control form-control_flotas control-select h-100" value={yearMonth} onChange={(e) => setYearMonth(e.target.value)}>
                        { years.reverse().map(year => {
                            return [...Array(12).keys()].reverse().map(month => {
                                month += 1; // Fix 0 based month

                                let yearMonth = year + '-' + (month < 10 ? '0' + month : month);

                                return (
                                    <option key={yearMonth} value={yearMonth}>
                                        {moment(yearMonth + '-01').format('MMMM')} / {moment(year + '-01-01').format('YY')}
                                    </option>
                                );
                            })
                        }) }
                    </select>
                    <button className="btn-plenoil-outline-blue ms-2" style={{width: 30, height: 30, borderRadius: '100%'}} onClick={() => downloadExcel()}><i className="bi bi-download"></i></button>
                </div>
            </div>
            <div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
                                                <thead>
                                                    <tr>
                                                        <ThSortable className="title_precios-dia" direction={sortDirection} active={sortField === 'date'} onClick={() => sortTableClick('date')}>Día</ThSortable>
                                                        <ThSortable className="title_precios-estacion" direction={sortDirection} active={sortField === 'station_name'} onClick={() => sortTableClick('station_name')}>Estación</ThSortable>
                                                        <th className="title_precios-entrada">Entrada</th>
                                                        <th className="title_precios-salida">Salida</th>
                                                        <th className="title_precios-entrada">Entrada</th>
                                                        <th className="title_precios-salida">Salida</th>
                                                        <ThSortable className="title_precios-registradas" direction={sortDirection} active={sortField === 'chronometers_total_in_seconds'} onClick={() => sortTableClick('chronometers_total_in_seconds')}>Registradas</ThSortable>
                                                        <ThSortable className="title_precios-balance" direction={sortDirection} active={sortField === 'balance_in_seconds'} onClick={() => sortTableClick('balance_in_seconds')}>Balance</ThSortable>
                                                        <th className="title_precios-n-destino">N. Destino</th>
                                                        <th className="title_precios-motivo">Motivo</th>
                                                        <th className="title_precios-notas">Notas</th>
                                                        <th className="title_precios-resumen">Resumen</th>
                                                        <th className="title_precios-gps">GPS</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    { schedules?.map((el, idx) => { 
                                                        return (
                                                            <React.Fragment key={'s' + idx}>
                                                                <tr>
                                                                    <td className="title_precios-dia">{moment(el.date).format('DD-MM-YYYY')}</td>
                                                                    <td className="title_precios-estacion">
                                                                        <div><b>{el.station_name}</b></div>
                                                                        <div>{el.schedule_name}</div>
                                                                        <div className="intervals">
                                                                            {el.intervals &&
                                                                                <>
                                                                                    HORARIO:&nbsp;
                                                                                    {el.intervals.map((i, iIdx) => {
                                                                                        return (
                                                                                            <span key={'s' + idx + '-in' + iIdx}>
                                                                                                {moment('2024-11-07 ' + i.start_at).format('HH:mm')} - {moment('2024-11-07 ' + i.end_at).format('HH:mm')}
                                                                                                {iIdx < el.intervals.length - 1 && <>&nbsp;/&nbsp;</>}
                                                                                            </span>
                                                                                        );
                                                                                    })}
                                                                                </>
                                                                            }
                                                                        </div>
                                                                    </td>
                                                                    <td className="title_precios-registradas"><TableAlert interval={el.intervals[0]} what={'start_at'} /></td>
                                                                    <td className="title_precios-registradas"><TableAlert interval={el.intervals[0]} what={'end_at'} /></td>
                                                                    <td className="title_precios-registradas"><TableAlert interval={el.intervals[1]} what={'start_at'} /></td>
                                                                    <td className="title_precios-registradas"><TableAlert interval={el.intervals[1]} what={'end_at'} /></td>
                                                                    <td className="title_precios-registradas">{el.chronometers_total !== 0 ? el.chronometers_total : ''}</td>
                                                                    <td className="title_precios-balance">{el.balance !== 0 ? el.balance : ''}</td>
                                                                    <td className="title_precios-estacion">{el.type === 'relocation' ? el.station_name : ''}</td>
                                                                    <td className="title_precios-motivo">{el.reason ?? ''}</td>
                                                                    <td className="title_precios-notas">{el.notes ?? ''}</td>
                                                                    <td className="title_precios-gps">
                                                                        <GpsPopup employeeId={el.employee_id} date={el.date} />
                                                                    </td>
                                                                    {idx === 0 && <Resume schedules={schedules} />}
                                                                </tr>
                                                            </React.Fragment>
                                                        )
                                                    }) }

                                                    { (!schedules.length && !loading) && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

                                                    { loading && <TrSkeleton rows={skeletonRows} columns={13} /> }
                                                </tbody>
                                            </table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Styled>
	);
}